import { AppLang } from "app";
import React, { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker, DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getI18n } from "react-i18next";
import { enUS, ruRU } from "@mui/x-date-pickers/locales";
import cn from "classnames";
import { useSharedTranslations } from "../../lib/i18n";
import styles from "./datetime-picker.module.scss";
import "./global-datetime-picker.css";

interface IDatetimePickerProps {
    initialValue: string | null;
    className?: string;
    dateFormat?: string;
    validate?: boolean;
    triggerValidate?: number;
    dateTimeFormat?: string;
    withTime?: boolean;
    disabled?: boolean;
    onUpdate?: (value: string | null) => void;
}

export const DatetimePicker: React.FC<IDatetimePickerProps> = ({
                                                                   initialValue,
                                                                   className,
                                                                   validate,
                                                                   triggerValidate,
                                                                   dateFormat = "YYYY-MM-DD",
                                                                   dateTimeFormat = "YYYY-MM-DD HH:mm:ss",
                                                                   withTime = true,
                                                                   onUpdate,
                                                                   disabled = false
                                                               }) => {
    const [dateValue, setDateValue] = useState<Dayjs | null>(null);
    const [isError, setIsError] = useState(false);
    const [validationError, setValidationError] = useState<string>();
    const t = useSharedTranslations();
    const language = getI18n().language;
    let timePickerLocalization: any;

    switch (language) {
        case AppLang.RU:
            timePickerLocalization = ruRU;
            break
        case AppLang.EN:
            timePickerLocalization = enUS;
            break
        default:
            timePickerLocalization = enUS;
    }

    useEffect(() => {
        if ((triggerValidate || !!dateValue) && validate) {
            validateValue(dateValue);
        } else {
            setIsError(false);
            setValidationError(undefined);
        }
    }, [dateValue, triggerValidate, validate])

    useEffect(() => {
        if (isError) {
            validateValue(dateValue);
        }
    }, [language])

    useEffect(() => {
        if (initialValue) {
            if (withTime) {
                // @ts-ignore
                const dateTime = dayjs.utc(initialValue).local();
                setDateValue(dateTime);
            } else {
                setDateValue(dayjs(initialValue, dateFormat));
            }
        } else {
            setDateValue(null);
        }
    }, [initialValue, withTime]);

    const onChangeDate = (date: Dayjs | null) => {
        onUpdateValue(date);
        setDateValue(date);
        if (validate) {
            validateValue(date);
        }
    }

    const validateValue = (val: Dayjs | null) => {
        let error = false;
        let message: string | undefined = undefined;
        if (!val) {
            error = true;
            message = t("requiredFieldError");
        } else {
            // @ts-ignore
            const dateString = withTime ? dayjs(val, dateTimeFormat).utc().format() : val.format(dateFormat);
            if (dateString === "Invalid Date") {
                error = true;
                message = t("requiredFieldError");
            }
        }
        setIsError(error);
        setValidationError(message);
    }

    const onUpdateValue = (dateValue: Dayjs | null) => {
        if (dateValue) {
            if (withTime) {
                // @ts-ignore
                const utcDateTime = dayjs(dateValue, dateTimeFormat).utc().format();
                if (utcDateTime !== "Invalid Date") {
                    onUpdate && onUpdate(utcDateTime);
                } else {
                    onUpdate && onUpdate(null);
                }
            } else {
                const dateString = dateValue.format(dateFormat);
                if (dateString !== "Invalid Date") {
                    onUpdate && onUpdate(dateString);
                } else {
                    onUpdate && onUpdate(null);
                }
            }
        } else {
            onUpdate && onUpdate(null);
        }
    }

    return <div className={cn(styles.dateTimeContainer, className)}>
        <LocalizationProvider
            localeText={timePickerLocalization.components.MuiLocalizationProvider.defaultProps.localeText}
            dateAdapter={AdapterDayjs}
            adapterLocale={language}
        >
            <div
                className={cn(styles.fieldContainer, isError ? styles.datePickerError : undefined, { [styles.fieldContainer__disabled]: disabled })}>
                {withTime ?
                    <DateTimePicker
                        className={styles.dateField}
                        value={dateValue}
                        disabled={disabled}
                        ampm={false}
                        views={["year", "month", "day", "hours", "minutes", "seconds"]}
                        timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
                        onChange={(newValue) => onChangeDate(newValue)} format="DD.MM.YYYY HH:mm:ss"
                    /> :
                    <DatePicker
                        className={styles.dateField}
                        value={dateValue}
                        disabled={disabled}
                        views={["year", "month", "day"]}
                        format="DD.MM.YYYY"
                        onChange={(newValue) => onChangeDate(newValue)}
                    />
                }
            </div>
        </LocalizationProvider>
        {isError && validationError && <div className="Textinput_state_error">
            <div className={cn(styles.validationError, "Textinput-Hint")}>
                {validationError}
            </div>
        </div>}
    </div>;
};
